<template>
  <CollapsiblePanel
    :variant="['default', 'rounded', '30-12-paddings']"
    :label="$t('_web_tournament-info_prizes_tournament', 'Prizes')"
    class="prizes-title"
  >
    <div class="prize-section-custom-wrapper">
      <div class="row prize-pool">
        <TextInput
          v-model="prizePool"
          class="col-3 h-text-left"
          type="text"
          :label="$$t('Total Prize Pool', 'pool-label')"
          :placeholder="$$t('Total prize pool', 'pool-placeholder')"
          :disabled="isTournamentFieldDisabled('prize_pool')"
        />
      </div>
      <div class="row my-5">
        <div class="col prize-line">
          <Subheader :text="$$t('Prize line')" class="mb-0" />
        </div>
      </div>
      <div v-if="prizeLine && prizeLine.length" class="row prize-line-label">
        <div class="col place-column">
          <TextLabel :text="$$t('Place:', 'place-label')" />
        </div>
        <div class="col-3">
          <TextLabel :text="$$t('Specify prize:', 'prize-label')" />
        </div>
        <div
          class="col-4 d-flex align-items-end flex-no-wrap justify-content-between"
        >
          <TextLabel
            :text="
              $$t(
                'Prize equivalent (will not be visible for players):',
                'text-label'
              )
            "
          />
          <InfoTooltip
            :text="
              $$t(
                'Internal info for possibility to calculate a prize statistic',
                'equivalent-tooltip'
              )
            "
            class="prizes-tooltip"
          />
        </div>
      </div>
      <div
        v-for="(item, index) in prizeLine"
        :key="`prize-place-${index}`"
        class="row prize-line-input"
      >
        <TextInput
          class="col place-column"
          :value="item.place"
          type="text"
          read-only
          disabled
          :placeholder="$$t('Place', 'place-placeholder')"
          @change="value => setDefaultPrizePlace(index, value)"
        />
        <TextInput
          v-validate.disable="prizeRule"
          :value="item.prize"
          type="text"
          :data-vv-name="`prize-${index}`"
          :error="errors.first(`prize-${index}`)"
          :data-vv-as="$$t('Prize', 'prize-placeholder')"
          :disabled="prizeLineDisabled"
          :placeholder="$$t('Prize', 'prize-placeholder')"
          class="col-3"
          @change="value => setDefaultPrize(index, value)"
        />
        <TextInput
          :value="item.value"
          type="text"
          :data-vv-name="`prize-eq-${index}`"
          :data-vv-as="$$t('Prize equivalent', 'text-placeholder')"
          :error="errors.first(`prize-eq-${index}`)"
          class="col-4"
          :disabled="prizeLineDisabled"
          :placeholder="$$t('Prize equivalent', 'text-placeholder')"
          @change="value => setDefaultPrizeValue(index, value)"
        />
        <div v-if="!prizeLineDisabled" class="col d-flex align-items-center">
          <Icon
            class="cursor-pointer"
            name="delete"
            :fill="$scss.primaryTextBg"
            width="14"
            @click="deleteRow(index)"
          />
        </div>
      </div>
      <div class="row d-flex prize-add-place">
        <div class="col">
          <HeraFormBtn
            class="btn-secondary-simple"
            :disabled="reachMaxPrizeCount || prizeLineDisabled"
            @click.native="addRow"
          >
            <Icon name="add" width="20" class="h-mr-3" />
            <TextBlock
              :text="
                $t(
                  '_web_tournament-prize-places_add-field-btn',
                  'Add place option'
                )
              "
            />
          </HeraFormBtn>
        </div>
      </div>
    </div>
  </CollapsiblePanel>
</template>

<script>
import { cloneDeep, get } from '@/utils/lodashUtils';
import { mapGetters, mapMutations } from 'vuex';
import { translate } from '@/mixins/translate';

import CollapsiblePanel from '@/components/atoms/CollapsiblePanel';
import TextLabel from '@/components/atoms/TextLabel';
import Subheader from '@/components/atoms/Subheader';
import TextBlock from '@/components/atoms/TextBlock';
import Icon from '@/components/atoms/Icon';
import TextInput from '@/components/atoms/form/HeraFormTextInput.vue';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import InfoTooltip from '@/components/molecules/InfoTooltip';
import { MAX_NUMBER_OF_PRIZE_FIELDS } from '@/utils/tournamentsConstants';

const DEFAULT_PRIZE_FIELDS = () => ({ place: null, prize: null, value: null });

const component = 'prize';

export default {
  name: 'PrizesSection',
  inject: ['$validator'],
  components: {
    TextBlock,
    Icon,
    TextInput,
    HeraFormBtn,
    TextLabel,
    Subheader,
    InfoTooltip,
    CollapsiblePanel,
  },

  mixins: [translate('_web_tournament_prize')],

  props: {
    titleTranslationKey: {
      type: String,
      default: '_web_tournament_sponsors_default-title',
    },
  },

  data() {
    return {};
  },
  computed: {
    ...mapGetters([
      'currentTournament',
      'currentTournamentInfo',
      'isTournamentFieldDisabled',
    ]),
    componentData() {
      return this.currentTournament[component];
    },
    prizeRule() {
      return this.prizePool ? 'required' : null;
    },
    prizeLineDisabled() {
      return this.isTournamentFieldDisabled('prize_line');
    },
    prizePool: {
      get() {
        return get(this.componentData, 'prize_pool', '');
      },
      set(newVal) {
        this.updateCurrentTournament({
          prize_pool: newVal,
          component,
        });
      },
    },
    prizeLine: {
      get() {
        return get(this.componentData, 'prize_line');
      },
      set(newVal) {
        this.updateCurrentTournament({
          prize_line: newVal,
          component,
        });
      },
    },
    currency: {
      get() {
        return get(this.componentData, 'currency', '');
      },
      set(newVal) {
        this.updateCurrentTournament({
          currency: newVal,
          component,
        });
      },
    },
    prizeCount() {
      return get(this, 'prizeLine.length', 0);
    },
    maxPrizeCount() {
      return MAX_NUMBER_OF_PRIZE_FIELDS.default;
    },
    reachMaxPrizeCount() {
      return this.prizeCount >= this.maxPrizeCount;
    },
  },
  mounted() {
    if (!(this.prizeLine && this.prizeLine)) {
      this.addRow();
    }
  },
  methods: {
    ...mapMutations(['updateCurrentTournament']),

    addRow() {
      let prizeLine = cloneDeep(this.prizeLine) || [];

      prizeLine.push({
        ...DEFAULT_PRIZE_FIELDS(),
        place: prizeLine.length + 1,
      });

      this.updateCurrentTournament({
        prize_line: prizeLine,
        component,
      });
    },
    deleteRow(index) {
      if (this.prizeLineDisabled) {
        return;
      }
      const prizeLine = cloneDeep(this.prizeLine);
      prizeLine.splice(index, 1);
      this.updateCurrentTournament({
        prize_line: prizeLine,
        component,
      });
    },
    setDefaultPrizePlace(index, value) {
      const prizeLine = cloneDeep(this.prizeLine);
      prizeLine[index].place = value;
      this.updateCurrentTournament({
        prize_line: prizeLine,
        component,
      });
    },
    setDefaultPrize(index, value) {
      const prizeLine = cloneDeep(this.prizeLine);
      prizeLine[index].prize = value;
      this.updateCurrentTournament({
        prize_line: prizeLine,
        component,
      });
    },
    setDefaultPrizeValue(index, value) {
      const prizeLine = cloneDeep(this.prizeLine);
      prizeLine[index].value = value;
      this.updateCurrentTournament({
        prize_line: prizeLine,
        component,
      });
    },
  },
};
</script>

<style lang="scss">
.prize-section-custom-wrapper {
  padding: 13px !important;
  .place-column {
    width: 40px !important;
    flex-basis: 40px !important;
    flex-grow: 0 !important;
    box-sizing: content-box;
  }
}

.prizes-title span {
  font-size: $hr-heading-5 !important;
}

.prize-line span {
  font-size: $hr-text-large !important;
}

.prize-pool label {
  padding-bottom: 13px;
  font-size: $hr-text-small !important;
  font-weight: 600;
}

.prize-pool input {
  background-color: $hr-bg-header;
  border: 2px solid transparent;
}

.prize-pool input :focus {
  border: 2px solid $hr-bg-accent;
}

.prize-pool {
  padding-bottom: 10px;
}

.prize-line-label label {
  font-size: $hr-text-small !important;
  font-weight: 600;
  padding-bottom: 10px;
}

.prize-line-input input {
  background-color: $hr-bg-header !important;
  border: 2px solid transparent;
  margin-bottom: 15px;
}

.prize-line-input input :focus {
  border: 2px solid $hr-bg-accent;
}

.prizes-tooltip {
  margin-bottom: 27px;
  margin-right: 80px;
}

.prize-add-place {
  padding-top: 5px;
}

.prize-add-place span {
  font-size: $hr-text-small !important;
}
</style>
